/**
 * Modal / ModalPortal
 * Renders modal dialogs via React portals (body-level) for proper z-index stacking.
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './icons';
import { StandardButton } from './Button';
import { useUIStore } from '@atlas-forms/state-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Extra class names on the modal box */
  className?: string;
  /** Width of the modal (default: 560px) */
  width?: number | string;
  /** Show close button in header (default true) */
  showClose?: boolean;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.65)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9000,
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  width = 560,
  showClose = true,
}: ModalProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const { theme } = useUIStore();
  const isDarkTheme = theme === 'dark';

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const boxStyle: React.CSSProperties = {
    background: isDarkTheme ? '#0f172a' : '#ffffff',
    border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: isDarkTheme ? '0 25px 60px rgba(0,0,0,0.5)' : '0 20px 50px rgba(15,23,42,0.22)',
    width: typeof width === 'number' ? `${width}px` : width,
    maxWidth: '95vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '0',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 24px',
    borderBottom: isDarkTheme ? '1px solid #1e293b' : '1px solid #e2e8f0',
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: isDarkTheme ? '#f1f5f9' : '#0f172a',
  };

  const content = (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={boxRef} style={boxStyle} className={className} role="dialog" aria-modal>
        {(title || showClose) && (
          <div style={headerStyle}>
            {title && <h2 style={titleStyle}>{title}</h2>}
            {showClose && (
              <StandardButton
                onClick={onClose}
                aria-label="Close modal"
                icon={<CloseIcon width={18} height={18} />}
                style={{
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  padding: '2px 6px',
                  fontSize: 20,
                }}
              />
            )}
          </div>
        )}
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/** Alias kept for compatibility with existing codebase that uses ModalPortal. */
export const ModalPortal = Modal;
